import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { getRecentCities } from '@/lib/recentCities';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Cities shown as quick-pick chips when there is no recent history. */
  fallbackCities?: string[];
  /** Compact mode for inline editors (smaller input). */
  compact?: boolean;
  autoFocus?: boolean;
}

interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  county?: string;
  state?: string;
  country?: string;
}

interface NominatimResult {
  display_name: string;
  address?: NominatimAddress;
  type?: string;
  class?: string;
}

function formatCity(r: NominatimResult): string {
  const a = r.address || {};
  const name =
    a.city || a.town || a.village || a.hamlet || a.municipality || a.county;
  if (name) {
    return a.country ? `${name}, ${a.country}` : name;
  }
  // Fallback: first 2 parts of display_name
  const parts = r.display_name.split(',').map(p => p.trim());
  if (parts.length >= 2) return `${parts[0]}, ${parts[parts.length - 1]}`;
  return r.display_name;
}

export function CityAutocomplete({
  value,
  onChange,
  placeholder,
  fallbackCities = [],
  compact = false,
  autoFocus = false,
}: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const recent = getRecentCities();
  const chips = (recent.length > 0 ? recent : fallbackCities).slice(0, 8);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = async (q: string) => {
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=6&addressdetails=1&featuretype=settlement`,
        { headers: { 'Accept-Language': 'en' } },
      );
      const data: NominatimResult[] = await res.json();
      // Dedupe by formatted city name
      const seen = new Set<string>();
      const filtered = data.filter(r => {
        const key = formatCity(r).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setSuggestions(filtered);
      setShowSuggestions(true);
    } catch (e) {
      console.error('City search error:', e);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v); // accept free-typed value as we go
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const pick = (city: string) => {
    setQuery(city);
    onChange(city);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div ref={containerRef} className="relative space-y-2">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className={compact ? 'pl-9 pr-9 h-8 text-xs' : 'pl-9 pr-9'}
          placeholder={placeholder || 'Search any city worldwide…'}
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          autoFocus={autoFocus}
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
            {suggestions.map((s, i) => {
              const label = formatCity(s);
              return (
                <button
                  key={`${label}-${i}`}
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors flex items-start gap-2"
                  onClick={() => pick(label)}
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                  <span className="line-clamp-2">{label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!compact && chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted-foreground self-center mr-1">
            {recent.length > 0 ? 'Recent:' : 'Popular:'}
          </span>
          {chips.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => pick(c)}
              className="px-2.5 py-1 text-xs rounded-full border border-border bg-background hover:bg-accent hover:border-primary/40 transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}